import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// my component for sorting Labels
import { SortChartLabels } from "./SortChartLabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ChartsPageBarTypeAQI = (props) => {
  const { labels, data, parameter, dataType, rangeType } = props;
  const [titleText, setTitleText] = useState("داده‌ها"); // Correct state initialization

  // Define thresholds
  const thresholds = {
    green: 50,
    yellow: 100,
    orange: 150,
    red: 200,
    purple: 300,
    darkred: 500,
  };

  // Prepare datasets
  const greenData = data.map((value) => Math.min(value, thresholds.green));
  const yellowData = data.map((value) =>
    Math.min(
      Math.max(value - thresholds.green, 0),
      thresholds.yellow - thresholds.green
    )
  );
  const orangeData = data.map((value) =>
    Math.min(
      Math.max(value - thresholds.yellow, 0),
      thresholds.orange - thresholds.yellow
    )
  );
  const redData = data.map((value) =>
    Math.min(
      Math.max(value - thresholds.orange, 0),
      thresholds.red - thresholds.orange
    )
  );
  const purpleData = data.map((value) =>
    Math.min(
      Math.max(value - thresholds.red, 0),
      thresholds.purple - thresholds.red
    )
  );
  const darkredData = data.map((value) =>
    Math.max(value - thresholds.purple, 0)
  );

  // my custom function for sorting chartLabels
  const sortedChartLabels = SortChartLabels(labels, dataType, rangeType);
  const reversedGreenData = [...greenData].reverse();
  const reversedYellowData = [...yellowData].reverse();
  const reversedOrangeData = [...orangeData].reverse();
  const reversedRedData = [...redData].reverse();
  const reversedPurpleData = [...purpleData].reverse();
  const reversedDarkredData = [...darkredData].reverse();

  const chartData = {
    labels: sortedChartLabels.persianLabels,
    datasets: [
      {
        data: reversedGreenData,
        label: "پاک",
        backgroundColor: "rgba(15, 157, 99, 1)",
        borderColor: "rgba(15, 157, 99, 1)",
      },
      {
        data: reversedYellowData,
        label: "قابل قبول",
        backgroundColor: "rgba(255, 215, 0, 1)",
        borderColor: "rgba(255, 215, 0, 1)",
      },
      {
        data: reversedOrangeData,
        label: "ناسالم برای گروه های حساس",
        backgroundColor: "rgba(255, 140, 0, 1)",
        borderColor: "rgba(255, 140, 0, 1)",
      },
      {
        data: reversedRedData,
        label: "ناسالم",
        backgroundColor: "rgba(255, 0, 0, 1)",
        borderColor: "rgba(255, 0, 0, 1)",
      },
      {
        data: reversedPurpleData,
        label: "بسیار ناسالم",
        backgroundColor: "rgba(128, 0, 128, 1)",
        borderColor: "rgba(128, 0, 128, 1)",
      },
      {
        data: reversedDarkredData,
        label: "خطرناک",
        backgroundColor: "rgba(165, 42, 42, 1)",
        borderColor: "rgba(165, 42, 42, 1)",
      },
    ],
  };

  const changeTitleText = () => {
    switch (parameter) {
      case "aqi":
        setTitleText("شاخص کیفیت هوا");
        break;
      case "pm25":
        setTitleText("ذرات معلق PM 2.5");
        break;
      case "pm10":
        setTitleText("ذرات معلق PM 10");
        break;
      case "co":
        setTitleText("گاز منواکسید کربن");
        break;
      case "o3":
        setTitleText("گاز ازن");
        break;
      case "so2":
        setTitleText("گاز گوگرد دی اکسید");
        break;
      case "no2":
        setTitleText("گاز نیتروژن دی اکسید");
        break;
      case "temperature":
        setTitleText("دما");
        break;
      case "humidity":
        setTitleText("رطوبت");
        break;
      case "pressure":
        setTitleText("فشار");
        break;
      case "spl":
        setTitleText("صدا");
        break;
      case "voc":
        setTitleText("بوی ناشی از تجزیه ترکیبات آلی");
        break;
      default:
        setTitleText("داده‌ها");
    }
  };

  useEffect(() => {
    changeTitleText();
  }, [parameter]);

  return (
    <div>
      <Bar
        className="chart-secondary mx-auto my-5 mw-100"
        style={{ width: "90%" }}
        data={chartData}
        options={{
          maxBarThickness: 45,
          responsive: true,
          scales: {
            x: {
              stacked: true, // Do not stack horizontally
            },
            y: {
              stacked: true, // Stack vertically
            },
          },
          plugins: {
            title: {
              display: true,
              text: titleText,
            },
            legend: {
              display: true,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                title: (tooltipItems) => {
                  return tooltipItems[0].label;
                },
                label: (tooltipItem) => {
                  const dataIndex = tooltipItem.dataIndex;

                  // Calculate the sum of all datasets for this bar
                  const sum = tooltipItem.chart.data.datasets.reduce(
                    (acc, dataset) => acc + dataset.data[dataIndex],
                    0
                  );

                  return `AQI: ${sum}`;
                },
              },
              boxPadding: 0, // This reduces padding inside the tooltip
              boxWidth: 0, // Hides the color box
              boxHeight: 0, // Hides the color box
            },
            datalabels: {
              display: false, // Ensure data labels are hidden if datalabels plugin is used
            },
          },
        }}
      />
    </div>
  );
};

export default ChartsPageBarTypeAQI;
